import axios from "axios";

const loadingStart = () => window.dispatchEvent(new Event("harwin:http:start"));
const loadingEnd = () => window.dispatchEvent(new Event("harwin:http:end"));

// Track actual human interaction separately from HTTP/background polling.
// The backend uses this signal for the 15-minute inactivity timeout.
const USER_ACTIVITY_KEY = "harwin:last-user-activity";
const USER_ACTIVITY_WINDOW_MS = 60 * 1000;
if (typeof window !== "undefined" && !window.__harwinActivityTrackingInstalled) {
  window.__harwinActivityTrackingInstalled = true;
  const markUserActivity = () => {
    try { localStorage.setItem(USER_ACTIVITY_KEY, String(Date.now())); } catch (_) {}
  };
  ["mousedown", "keydown", "touchstart", "scroll"].forEach((eventName) =>
    window.addEventListener(eventName, markUserActivity, { passive: true }),
  );
  markUserActivity();
}

// Prevent accidental double-click submissions across normal JSON CRUD forms.
// File uploads/FormData and callers that explicitly opt out are not deduped.
const recentMutations = new Map();
const MUTATION_DEDUPE_MS = 900;
const mutationSignature = (config) => {
  const method = String(config.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return null;
  if (config.allowDuplicateRequest === true) return null;
  if (typeof FormData !== "undefined" && config.data instanceof FormData)
    return null;
  let body = "";
  try {
    body =
      typeof config.data === "string"
        ? config.data
        : JSON.stringify(config.data || {});
  } catch (_) {}
  return `${method}|${config.url || ""}|${body}`;
};

// ✅ REQUEST INTERCEPTOR - Adds token to every request
axios.interceptors.request.use(
  (config) => {
    const signature = mutationSignature(config);
    if (signature) {
      const now = Date.now();
      const last = recentMutations.get(signature) || 0;
      if (now - last < MUTATION_DEDUPE_MS) {
        const error = new Error(
          "Please wait — this action is already being submitted.",
        );
        error.code = "DUPLICATE_SUBMIT";
        error.config = { ...config, skipGlobalLoading: true };
        return Promise.reject(error);
      }
      recentMutations.set(signature, now);
      window.setTimeout(() => {
        if (recentMutations.get(signature) === now)
          recentMutations.delete(signature);
      }, MUTATION_DEDUPE_MS + 100);
    }
    if (!config.skipGlobalLoading) {
      config.__harwinGlobalLoading = true;
      loadingStart();
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      const lastUserActivity = Number(localStorage.getItem(USER_ACTIVITY_KEY) || 0);
      if (Date.now() - lastUserActivity <= USER_ACTIVITY_WINDOW_MS) {
        config.headers["X-User-Activity"] = "1";
      }
    }
    return config;
  },
  (error) => {
    loadingEnd();
    return Promise.reject(error);
  },
);

// ✅ RESPONSE INTERCEPTOR - Handles auth errors
axios.interceptors.response.use(
  (response) => {
    if (response.config?.__harwinGlobalLoading) loadingEnd();
    return response;
  },
  (error) => {
    if (error.config?.__harwinGlobalLoading) loadingEnd();
    const status = error.response?.status;
    const requestUrl = error.config?.url || "";

    // ✅ FIX: Skip interceptor logic for login/reset endpoints
    // A 401 on /login means wrong password — NOT an expired token
    const isAuthEndpoint =
      requestUrl.includes("/login") ||
      requestUrl.includes("/reset-password") ||
      requestUrl.includes("/debug-user");

    // A 403 can be returned by background/auto-fetch requests too. Do not show a
    // global permission toast for every 403: page/action handlers own user-facing
    // permission feedback (Edit/Delete/Create/Download etc.) so normal rendering,
    // filtering and refreshes never spam "no permission" messages. A caller that
    // genuinely needs central handling can explicitly opt in.
    if (status === 403 && error.config?.showPermissionToast === true) {
      const message =
        error.response?.data?.message ||
        "You don't have permission to perform this action.";
      window.dispatchEvent(
        new CustomEvent("harwin:access-denied", { detail: { message } }),
      );
    }

    if (status === 401 && !isAuthEndpoint) {
      console.error("❌ 401 Unauthorized - Session invalid/expired");

      const currentPath = window.location.pathname;
      const code = error.response?.data?.code;
      const message =
        code === "SESSION_REVOKED"
          ? "This device has been signed out."
          : code === "SESSION_REPLACED"
            ? "Your account was logged in on another device."
            : code === "SESSION_INACTIVE"
              ? "Your session expired due to 15 minutes of inactivity."
              : error.response?.data?.message || "Your session has ended. Please login again.";

      if (!currentPath.includes("/login")) {
        sessionStorage.setItem("harwinSessionEndMessage", message);
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default axios;
